import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { track, trackError } from '@/lib/analytics'
import {
  closePoll,
  duplicatePoll,
  fetchPollDetail,
  fetchPollTimeseries,
  fetchPollVoters,
  isLabAnalyticsRequired,
  type PollDetail,
  type PollType,
  type PollStatus,
  type PollCloseReason,
  type PollTimeseries,
  type PollVoter,
  type VotersTab,
  type LabAnalyticsRequired,
} from '@/api/polls'
import { emitPollEvent } from '@/api/pollEvents'
import { useChannel } from '@/context/ChannelContext'

/**
 * Adapter: превращает nested-shape `PollDetail` (contract B 2026-07-24)
 * в flat-view для существующего UI. Не пересчитывает — только раскладывает.
 * После DEP-1b full-refactor уберём его — сейчас минимальный churn.
 */
interface AdaptedPoll {
  id: string
  channelUUID: string
  question: string
  type: PollType
  status: PollStatus
  closeReason: PollCloseReason
  closeAt: string | null
  updatedAt: string
  anonymous: boolean
  allowRevote: boolean
  isAdmin: boolean
  createdBy: string
  /** Опции с count/pct — merged из options + totals.byOption. Если `totals=null` (юзер не голосовал и результаты скрыты) → count/pct обнулены. */
  options: Array<{ id: string; letter: string; text: string; isCorrect: boolean; count: number; pct: number }>
  totalVotes: number
  /** `null` если результаты пока скрыты. UI показывает placeholder. */
  totalsHidden: boolean
  myVoteOptionIds: string[] | null
  /** Только для quiz после голосования / после close. */
  correctOptionIds?: string[]
}

function adaptPoll(detail: PollDetail): AdaptedPoll {
  const { poll, options, totals, myVote, correctOptionIds, isAdmin } = detail
  const byOption = new Map(totals?.byOption.map((b) => [b.optionId, b]) ?? [])
  const isCorrectFromServer = new Set(correctOptionIds ?? [])
  return {
    id: poll.id,
    channelUUID: poll.channelUUID,
    question: poll.question,
    type: poll.type,
    status: poll.status,
    closeReason: poll.closeReason,
    closeAt: poll.closeAt,
    updatedAt: poll.updatedAt,
    anonymous: poll.anonymous,
    allowRevote: poll.allowRevote,
    isAdmin,
    createdBy: poll.createdBy,
    options: options.map((o) => ({
      id: o.id,
      letter: o.letter,
      text: o.text,
      // v3.1a §2.3: до голосования quiz-варианты приходят с isCorrect=false у всех.
      // Правильный ответ доверяем только correctOptionIds (server hides до voting).
      isCorrect: isCorrectFromServer.has(o.id) || o.isCorrect,
      count: byOption.get(o.id)?.count ?? 0,
      pct: byOption.get(o.id)?.pct ?? 0,
    })),
    totalVotes: totals?.byOption.reduce((s, b) => s + b.count, 0) ?? 0,
    totalsHidden: totals === null,
    myVoteOptionIds: myVote?.optionIds ?? null,
    correctOptionIds,
  }
}

/**
 * P1-3 — Poll results `/polls/:pollId`. ТЗ v3.1a §2.3 + §4.2.2.
 *
 * Секции:
 * - Hero: заголовок + метаданные + status-баннер (message_deleted / bot_deadmin)
 * - 4 KPI: Голосов из N подписчиков · Вовлечённость · Лидер · Голосов/час
 * - Распределение ответов (бары, winner подсвечен, sub на winner'е)
 * - Динамика голосования (line-chart SVG по часам)
 * - Кто проголосовал (табы + список; для anonymous — «Анонимный подписчик»)
 * - Actions: Закрыть досрочно (для isAdmin + live)
 *
 * Live-обновление: polling `fetchPollDetail(pollId, etag)` каждые 5s пока
 * `status='live' && visible`. При `visibilityState==='hidden'` — стоп.
 * После `status='closed'` — backoff 30s (одноразовый refresh для итогов).
 */
const POLL_INTERVAL_MS = 5000
const POLL_INTERVAL_CLOSED_MS = 30_000

export function PollResultsPage() {
  const { pollId = '' } = useParams<{ pollId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const conflictParam = searchParams.get('conflict')
  const { selectedChannel } = useChannel()

  const [detail, setDetail] = useState<PollDetail | null>(null)
  const [timeseries, setTimeseries] = useState<PollTimeseries | null>(null)
  const [timeseriesLock, setTimeseriesLock] = useState<LabAnalyticsRequired | null>(null)
  const [voters, setVoters] = useState<PollVoter[] | null>(null)
  const [votersCursor, setVotersCursor] = useState<string | null>(null)
  const [votersHasMore, setVotersHasMore] = useState(false)
  const [votersLoadingMore, setVotersLoadingMore] = useState(false)
  const [votersLock, setVotersLock] = useState<LabAnalyticsRequired | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const etagRef = useRef<string | null>(null)
  const [tab, setTab] = useState<VotersTab>('all')
  // hotfix1: firing `unlocked` exactly once per lock→unlock transition per feature
  const teaserShownRef = useRef<{ timeseries: boolean; voters: boolean }>({ timeseries: false, voters: false })

  const [conflictDismissed, setConflictDismissed] = useState(false)

  const poll = useMemo(() => (detail ? adaptPoll(detail) : null), [detail])

  // Fetch initial + polling
  useEffect(() => {
    if (!pollId) return
    let cancelled = false
    let intervalId: number | null = null

    const doFetch = async () => {
      const detailRes = await fetchPollDetail(pollId, etagRef.current ?? undefined)
      if (cancelled) return
      if (detailRes.notModified) return // 304 — данные уже свежие
      if (!detailRes.data) {
        trackError(new Error('fetchPollDetail null'), 'poll-detail', { pollId })
        setIsLoading(false)
        return
      }
      etagRef.current = detailRes.etag
      setDetail(detailRes.data)
      setIsLoading(false)
    }

    void doFetch()

    // one-shot voters (tab='all', 1-я страница) + timeseries.
    // Оба endpoint'а под 402 gate (v3.1a-hotfix1 §5.7.8): non-Lab автор канала
    // видит teaser вместо графика/списка.
    void fetchPollVoters({ pollId, tab: 'all' }).then((v) => {
      if (cancelled || v == null) return
      if (isLabAnalyticsRequired(v)) {
        setVotersLock(v)
        if (!teaserShownRef.current.voters) {
          teaserShownRef.current.voters = true
          track('poll_admin_analytics_teaser_shown', { pollId, feature: 'voters' })
          // B contract §2.6: whitelist funnel-event. meta.source='lk' — дедуп-ключ с webview.
          emitPollEvent('poll_admin_analytics_teaser_shown', { pollId, meta: { source: 'lk', block: 'voters' } })
        }
        return
      }
      // Успех после lock → «unlocked» (пример: юзер подписался на Lab и вернулся)
      if (teaserShownRef.current.voters) {
        track('poll_admin_analytics_unlocked', { pollId, feature: 'voters' })
        teaserShownRef.current.voters = false
      }
      setVotersLock(null)
      setVoters(v.items)
      setVotersCursor(v.nextCursor)
      setVotersHasMore(v.hasMore)
    })
    void fetchPollTimeseries(pollId).then((t) => {
      if (cancelled || t == null) return
      if (isLabAnalyticsRequired(t)) {
        setTimeseriesLock(t)
        if (!teaserShownRef.current.timeseries) {
          teaserShownRef.current.timeseries = true
          track('poll_admin_analytics_teaser_shown', { pollId, feature: 'timeseries' })
          emitPollEvent('poll_admin_analytics_teaser_shown', { pollId, meta: { source: 'lk', block: 'timeseries' } })
        }
        return
      }
      if (teaserShownRef.current.timeseries) {
        track('poll_admin_analytics_unlocked', { pollId, feature: 'timeseries' })
        teaserShownRef.current.timeseries = false
      }
      setTimeseriesLock(null)
      setTimeseries(t)
    })

    const scheduleNext = () => {
      const isClosed = detail?.poll.status === 'closed'
      const delay = isClosed ? POLL_INTERVAL_CLOSED_MS : POLL_INTERVAL_MS
      intervalId = window.setInterval(() => {
        // §7.5 #7 visibilitychange stop
        if (document.visibilityState !== 'visible') return
        // После close — один-два refresh достаточно
        if (isClosed) return
        void doFetch()
      }, delay)
    }
    scheduleNext()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void doFetch()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      if (intervalId) clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId])

  useEffect(() => {
    if (poll) {
      track('poll_results_opened', { pollId, totalVotes: poll.totalVotes, status: poll.status })
      // Security guard (Виктор 2026-07-28): страница `/polls/:pollId` — админ-dashboard.
      // Если открыл не-автор (isAdmin=false, `poll.createdBy !== requesterMaxUserID`),
      // редиректим на список своих опросов. Иначе прямая ссылка на чужой опрос
      // раскрывает distribution/bars/timeseries (voters/timeseries под 403 у B, но
      // detail/bars открыты для webview голосования — тут это не webview).
      if (!poll.isAdmin) {
        track('poll_results_forbidden_redirect', {
          level: 'warn',
          pollId,
          createdBy: poll.createdBy,
          message: 'non-author попал на /polls/:pollId — редирект на /polls',
        })
        navigate('/polls', { replace: true })
        return
      }
      // hotfix1 event: автор канала открыл админ-аналитику
      if (poll.status === 'live' || poll.status === 'closed') {
        track('poll_admin_analytics_open', { pollId, status: poll.status })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll?.id])

  // Voters: перезагрузка на смену tab'ы (сервер фильтрует, cursor сбрасывается)
  const reloadVoters = useCallback(
    async (nextTab: VotersTab) => {
      const v = await fetchPollVoters({ pollId, tab: nextTab })
      if (v == null) return
      if (isLabAnalyticsRequired(v)) {
        setVotersLock(v)
        return
      }
      setVotersLock(null)
      setVoters(v.items)
      setVotersCursor(v.nextCursor)
      setVotersHasMore(v.hasMore)
    },
    [pollId],
  )

  const loadMoreVoters = useCallback(async () => {
    if (!votersCursor || votersLoadingMore) return
    setVotersLoadingMore(true)
    try {
      const v = await fetchPollVoters({ pollId, tab, cursor: votersCursor })
      if (v == null || isLabAnalyticsRequired(v)) return
      setVoters((prev) => [...(prev ?? []), ...v.items])
      setVotersCursor(v.nextCursor)
      setVotersHasMore(v.hasMore)
    } finally {
      setVotersLoadingMore(false)
    }
  }, [pollId, tab, votersCursor, votersLoadingMore])

  const handleTabChange = useCallback(
    (next: VotersTab) => {
      setTab(next)
      void reloadVoters(next)
    },
    [reloadVoters],
  )

  const winnerOption = useMemo(() => {
    if (!poll || poll.options.length === 0) return null
    return poll.options.reduce((best, o) => (o.count > best.count ? o : best), poll.options[0])
  }, [poll])

  if (isLoading && !poll) {
    return <div className="poll-loading">Загружаем результаты…</div>
  }
  if (!poll) {
    return (
      <div className="poll-empty">
        Не удалось загрузить опрос. <Link to="/polls">Вернуться к списку</Link>.
      </div>
    )
  }

  const channelTitle = selectedChannel?.title ?? ''
  const channelSubscribers = (selectedChannel as { memberCount?: number } | null)?.memberCount ?? 0

  return (
    <div className="poll-results">
      {conflictParam === '1' && !conflictDismissed && (
        <div className="conflict-toast" role="alert">
          <span>
            Этот опрос уже был опубликован — вы видите живой результат. Дубли не создались (idempotency).
          </span>
          <button onClick={() => setConflictDismissed(true)}>ОК</button>
        </div>
      )}

      <TerminalStateBanner poll={poll} />

      <PollHero poll={poll} onClose={async () => {
        const res = await closePoll(pollId)
        if ('closedAt' in res) {
          track('poll_closed_early', { pollId, votesAtClose: poll.totalVotes, closeReason: 'manual', closedAt: res.closedAt })
          navigate(0) // refetch
          return
        }
        // Typed error union (403 / 404 / 409 / network)
        if (res.code === 'ALREADY_CLOSED') {
          // Race — другая вкладка / auto_at_date уже закрыли. Просто перезагружаем.
          track('poll_close_race', { pollId })
          navigate(0)
          return
        }
        if (res.code === 'FORBIDDEN') {
          alert('Только автор опроса может закрыть его досрочно.')
          return
        }
        if (res.code === 'NOT_FOUND') {
          alert('Опрос не найден — возможно, удалён из канала.')
          navigate('/polls')
          return
        }
        // NETWORK / 500 / 502 — ошибка сервера или сети
        alert('Не удалось закрыть опрос. Ошибка на сервере или сети — попробуйте ещё раз.')
      }} onDuplicate={async () => {
        const dup = await duplicatePoll(pollId)
        if (dup) {
          // B возвращает новый draft-poll — открываем сразу в edit-mode
          // (CreatePollPage подгружает через fetchPollDetail).
          track('poll_duplicated', { pollId, newPollId: dup.pollId })
          navigate(`/polls/new?edit=${dup.pollId}`)
        } else {
          alert('Не удалось создать копию. Попробуйте ещё раз.')
        }
      }} />

      <ResultsKpis poll={poll} winner={winnerOption} channelSubscribers={channelSubscribers} />

      <div className="layout">
        <div>
          <ResultsBars poll={poll} winner={winnerOption} />
          <TimeseriesChart timeseries={timeseries} lock={timeseriesLock} pollId={pollId} />
          <VotersCard
            poll={poll}
            voters={voters}
            tab={tab}
            setTab={handleTabChange}
            hasMore={votersHasMore}
            loadingMore={votersLoadingMore}
            onLoadMore={loadMoreVoters}
            lock={votersLock}
          />
        </div>
        <PollSidePanel poll={poll} channelTitle={channelTitle} />
      </div>

      <PageStyles />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Terminal state banners (§2.3 v3.1a)
// ─────────────────────────────────────────────────────────────────────────────
function TerminalStateBanner({ poll }: { poll: AdaptedPoll }) {
  if (poll.status !== 'closed') return null
  if (poll.closeReason === 'message_deleted') {
    return (
      <div className="terminal-banner warn">
        <strong>Сообщение опроса удалено из канала.</strong> Приём голосов остановлен {formatDate(poll.updatedAt)}.
      </div>
    )
  }
  if (poll.closeReason === 'bot_deadmin') {
    return (
      <div className="terminal-banner warn">
        <strong>Бот потерял права администратора в канале.</strong> Верните права и создайте дубликат опроса.
      </div>
    )
  }
  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────────────
function PollHero({
  poll,
  onClose,
  onDuplicate,
}: {
  poll: AdaptedPoll
  onClose: () => void
  onDuplicate: () => void
}) {
  const isLive = poll.status === 'live'
  return (
    <header className="hero">
      <div className="hero-l">
        <Link className="hero-back" to="/polls">
          ← К списку опросов
        </Link>
        <div className="hero-meta">
          <span>{typeLabelHuman(poll.type)} · {poll.options.length} вариантов</span>
          {poll.anonymous && (
            <>
              <span className="hero-meta-sep" />
              <span>Анонимный</span>
            </>
          )}
          {isLive && poll.closeAt && (
            <>
              <span className="hero-meta-sep" />
              <span>
                Закроется через <b>{formatTimeLeft(poll.closeAt)}</b>
              </span>
            </>
          )}
        </div>
        <h1>
          {poll.question} {poll.type === 'quiz' && <span className="quiz-mark">викторина</span>}
        </h1>
      </div>
      <div className="hero-actions">
        {isLive && poll.isAdmin && (
          <button className="btn btn-danger-ghost" type="button" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M9 9l6 6M9 15l6-6" />
            </svg>
            Закрыть досрочно
          </button>
        )}
      </div>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// KPI (4)
// ─────────────────────────────────────────────────────────────────────────────
function ResultsKpis({
  poll,
  winner,
  channelSubscribers,
}: {
  poll: AdaptedPoll
  winner: AdaptedPoll['options'][number] | null
  channelSubscribers: number
}) {
  const engagement = channelSubscribers > 0 ? (poll.totalVotes / channelSubscribers) * 100 : 0
  return (
    <div className="kpis">
      <div className="kpi">
        <div className="kpi-label">Голосов</div>
        <div className="kpi-value">{poll.totalVotes.toLocaleString('ru-RU')}</div>
        <div className="kpi-sub">
          {channelSubscribers > 0 ? (
            <>
              из <b>{channelSubscribers.toLocaleString('ru-RU')}</b> подписчиков
            </>
          ) : (
            'всего голосов'
          )}
        </div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Вовлечённость</div>
        <div className="kpi-value">
          {channelSubscribers > 0 ? engagement.toFixed(1).replace('.', ',') : '—'} <em>%</em>
        </div>
        <div className="kpi-sub">{channelSubscribers > 0 ? 'от подписчиков' : 'нет данных о канале'}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Лидер</div>
        <div className="kpi-value">
          {winner?.pct ?? 0} <em>%</em>
        </div>
        <div className="kpi-sub">{winner ? `«${truncate(winner.text, 26)}»` : '—'}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Обновлено</div>
        <div className="kpi-value" style={{ fontSize: 20 }}>
          {formatTimeShort(poll.updatedAt)}
        </div>
        <div className="kpi-sub">по данным опроса</div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Bars
// ─────────────────────────────────────────────────────────────────────────────
function ResultsBars({ poll, winner }: { poll: AdaptedPoll; winner: AdaptedPoll['options'][number] | null }) {
  const maxPct = Math.max(...poll.options.map((o) => o.pct))
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2 className="card-title">Распределение ответов</h2>
          <p className="card-sub">{poll.status === 'live' ? 'Обновляется в реальном времени' : 'Итоговое распределение'}</p>
        </div>
      </header>
      <div className="card-body">
        <div className="results">
          {poll.options.map((o) => {
            const isWinner = winner?.id === o.id
            const barWidth = maxPct === 0 ? 0 : (o.pct / maxPct) * 100
            return (
              <div key={o.id} className={`result ${isWinner ? 'winner' : ''} ${o.isCorrect ? 'quiz-correct' : ''}`}>
                <div className="result-letter">{o.letter}</div>
                <div className="result-body">
                  <div className="result-text">
                    <span className="result-text-l">
                      {o.text}
                      {poll.type === 'quiz' && o.isCorrect && <span className="correct-tag">правильный</span>}
                    </span>
                    <span className="result-text-r">
                      <span className="result-pct">{o.pct} %</span>
                      <span className="result-count">{o.count.toLocaleString('ru-RU')} голосов</span>
                    </span>
                  </div>
                  <div className="result-bar">
                    <i style={{ width: `${barWidth}%` }} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Timeseries — простой SVG line-chart. B contract (2026-07-27): ОДНА линия
// «всего голосов во времени» (не per-option). 402 → teaser с CTA «Подключить Lab».
// ─────────────────────────────────────────────────────────────────────────────
function TimeseriesChart({
  timeseries,
  lock,
  pollId,
}: {
  timeseries: PollTimeseries | null
  lock: LabAnalyticsRequired | null
  pollId: string
}) {
  if (lock) return <LabAnalyticsTeaser lock={lock} feature="timeseries" pollId={pollId} title="Динамика голосования" body="График роста голосов во времени доступен участникам Telegraphyx Lab." />
  if (!timeseries || timeseries.points.length === 0) return null

  const width = 640
  const height = 200
  const padding = { top: 12, right: 20, bottom: 26, left: 32 }

  const maxCount = Math.max(1, ...timeseries.points.map((p) => p.count))
  const xScale = (idx: number) =>
    padding.left + (idx / Math.max(1, timeseries.points.length - 1)) * (width - padding.left - padding.right)
  const yScale = (v: number) =>
    height - padding.bottom - (v / maxCount) * (height - padding.top - padding.bottom)

  const path = timeseries.points
    .map((pt, idx) => `${idx === 0 ? 'M' : 'L'} ${xScale(idx)} ${yScale(pt.count)}`)
    .join(' ')

  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2 className="card-title">Динамика голосования</h2>
          <p className="card-sub">
            Всего голосов по времени · итого <b>{timeseries.totalVoters.toLocaleString('ru-RU')}</b>
          </p>
        </div>
      </header>
      <div className="chart-wrap">
        <svg viewBox={`0 0 ${width} ${height}`} className="chart-svg" preserveAspectRatio="none">
          {/* grid */}
          {[0.25, 0.5, 0.75, 1].map((r, i) => (
            <line
              key={i}
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(maxCount * r)}
              y2={yScale(maxCount * r)}
              stroke="#e8e8ec"
              strokeDasharray="3 3"
            />
          ))}
          {/* single line: cumulative votes */}
          <path d={path} stroke="var(--tg-blue, #0066ff)" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          {/* markers */}
          {timeseries.points.map((pt, i) => (
            <circle key={i} cx={xScale(i)} cy={yScale(pt.count)} r="3" fill="var(--tg-blue, #0066ff)" />
          ))}
          {/* x-labels */}
          {timeseries.points.map((pt, i) => {
            if (i !== 0 && i !== timeseries.points.length - 1 && i !== Math.floor(timeseries.points.length / 2)) return null
            const d = new Date(pt.ts)
            return (
              <text key={i} x={xScale(i)} y={height - 6} fontSize="10" fill="#8f9eb0" textAnchor="middle">
                {`${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`}
              </text>
            )
          })}
        </svg>
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// LabAnalyticsTeaser — inline-заглушка для 402 (v3.1a-hotfix1 §5.7.8)
// ─────────────────────────────────────────────────────────────────────────────
function LabAnalyticsTeaser({
  lock,
  feature,
  pollId,
  title,
  body,
}: {
  lock: LabAnalyticsRequired
  feature: 'timeseries' | 'voters'
  pollId: string
  title: string
  body: string
}) {
  return (
    <section className="card lab-teaser">
      <header className="card-head">
        <div>
          <h2 className="card-title">{title}</h2>
          <p className="card-sub">Доступно участникам <b>{lock.chatName}</b></p>
        </div>
      </header>
      <div className="card-body">
        <div className="teaser-body">
          <div className="teaser-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l2.39 6.95H22l-5.95 4.32L18.44 22 12 17.77 5.56 22l2.39-8.73L2 8.95h7.61L12 2z" />
            </svg>
          </div>
          <p className="teaser-text">{body}</p>
          <a
            className="teaser-cta"
            href={lock.labChannelDeepLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track('poll_admin_analytics_teaser_cta_click', { pollId, feature })
              emitPollEvent('poll_admin_analytics_teaser_cta_click', { pollId, meta: { source: 'lk', block: feature } })
            }}
          >
            Подключить Lab →
          </a>
        </div>
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Voters card
// ─────────────────────────────────────────────────────────────────────────────
function VotersCard({
  poll,
  voters,
  tab,
  setTab,
  hasMore,
  loadingMore,
  onLoadMore,
  lock,
}: {
  poll: AdaptedPoll
  voters: PollVoter[] | null
  tab: VotersTab
  setTab: (t: VotersTab) => void
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
  lock: LabAnalyticsRequired | null
}) {
  if (lock) {
    return (
      <LabAnalyticsTeaser
        lock={lock}
        feature="voters"
        pollId={poll.id}
        title="Кто проголосовал"
        body="Поимённый список голосующих и разбивка «до/после подписки» доступны участникам Telegraphyx Lab."
      />
    )
  }
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2 className="card-title">Кто проголосовал</h2>
          <p className="card-sub">
            {poll.anonymous ? 'Опрос анонимный — показываем только счётчики' : 'Показываем имена из MAX'}
          </p>
        </div>
      </header>
      <div className="card-body">
        <div className="tabs">
          <button className={tab === 'all' ? 'on' : ''} onClick={() => setTab('all')}>
            Все <span className="tab-count">{poll.totalVotes}</span>
          </button>
          <button className={tab === 'subscribed_before' ? 'on' : ''} onClick={() => setTab('subscribed_before')}>
            Подписаны до опроса
          </button>
          <button className={tab === 'subscribed_after' ? 'on' : ''} onClick={() => setTab('subscribed_after')}>
            Подписались после
          </button>
        </div>
        {poll.anonymous && (
          <div className="anon-note">
            <b>Опрос анонимный.</b> Мы не храним <b>кто</b> как ответил — только сколько и в какое время. Если нужно поимённо — при создании следующего опроса выключите «Анонимность».
          </div>
        )}
        {!voters && <div className="list-empty">Загружаем список…</div>}
        {voters && voters.length === 0 && <div className="list-empty">В этом разделе никого нет.</div>}
        {voters && voters.length > 0 && (
          <div className="voters-list">
            {voters.map((v, i) => {
              // Anonymous polls: не рендерим displayName + optionIds (даже если B их вернул).
              // Copy «Опрос анонимный — только счётчики» иначе противоречит рендеру.
              // Non-anonymous: показываем имя + выбранный option.
              const chosen = v.optionIds
                .map((id) => poll.options.find((o) => o.id === id))
                .filter(Boolean) as AdaptedPoll['options']
              const firstChosen = chosen[0]
              const letters = chosen.map((o) => o.letter).join(', ') || '?'
              const subLabel =
                v.wasSubscribedAtVote === true
                  ? 'Был подписан на канал'
                  : v.wasSubscribedAtVote === false
                  ? 'Подписался через опрос'
                  : ''
              return (
                <div className="voter" key={i}>
                  <div className={`voter-avatar ${poll.anonymous ? 'anon' : ''}`}>
                    {poll.anonymous ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21a8 8 0 0 1 16 0" />
                      </svg>
                    ) : (
                      v.displayName?.slice(0, 2) ?? '?'
                    )}
                  </div>
                  <div>
                    <div className="voter-name">
                      {poll.anonymous ? 'Анонимный подписчик' : v.displayName ?? 'Без имени'}
                    </div>
                    <div className="voter-sub">
                      {poll.anonymous ? (
                        subLabel || 'Проголосовал(а)'
                      ) : (
                        <>
                          Выбрал(а) вариант <b>{letters}</b>
                          {firstChosen && chosen.length === 1 && ` · «${truncate(firstChosen.text, 26)}»`}
                        </>
                      )}
                    </div>
                  </div>
                  <span className="voter-time">{formatTimeShort(v.votedAt)}</span>
                </div>
              )
            })}
          </div>
        )}
        {hasMore && (
          <button
            type="button"
            className="load-more"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Загружаем…' : 'Показать ещё'}
          </button>
        )}
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Side panel (справа): параметры опроса + share
// ─────────────────────────────────────────────────────────────────────────────
function PollSidePanel({ poll, channelTitle }: { poll: AdaptedPoll; channelTitle: string }) {
  return (
    <aside className="side">
      <div className="side-panel">
        <h3 className="side-title">Параметры опроса</h3>
        <div className="meta-list">
          <div className="meta-row">
            <span className="k">Канал</span>
            <span className="v">{channelTitle ? `«${channelTitle}»` : '—'}</span>
          </div>
          <div className="meta-row">
            <span className="k">Тип</span>
            <span className="v">{typeLabelHuman(poll.type)}</span>
          </div>
          <div className="meta-row">
            <span className="k">Анонимность</span>
            <span className="v">{poll.anonymous ? 'Да' : 'Нет'}</span>
          </div>
          <div className="meta-row">
            <span className="k">Смена голоса</span>
            <span className="v">{poll.allowRevote ? 'Разрешена' : 'Нет'}</span>
          </div>
          {poll.status === 'live' && poll.closeAt && (
            <div className="meta-row">
              <span className="k">Закроется</span>
              <span className="v">
                <b>{formatDate(poll.closeAt)}</b>
              </span>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function typeLabelHuman(t: PollType): string {
  return t === 'quiz' ? 'Викторина' : t === 'multi' ? 'Несколько ответов' : 'Один ответ'
}

function truncate(s: string, n: number): string {
  if (Array.from(s).length <= n) return s
  return Array.from(s).slice(0, n).join('') + '…'
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const isToday = d.toDateString() === today.toDateString()
  if (isToday) return `сегодня, ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  const daysAgo = Math.floor((today.getTime() - d.getTime()) / 86400000)
  if (daysAgo === 1) return 'вчера'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })
}

function formatTimeLeft(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return '—'
  const h = Math.floor(ms / 3600000)
  const m = Math.floor((ms % 3600000) / 60000)
  if (h >= 24) return `${Math.floor(h / 24)} дн ${h % 24} ч`
  return `${h} ч ${m} мин`
}

function formatTimeShort(iso: string): string {
  const d = new Date(iso)
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
function PageStyles() {
  return (
    <style>{`
      .poll-results { max-width: 1200px; margin: 0 auto; width: 100%; min-width: 0; box-sizing: border-box; }
      .poll-loading, .poll-empty { padding: 60px 32px; text-align: center; color: var(--tg-mute, #8f9eb0); }
      .poll-empty a { color: var(--tg-blue, #0066ff); text-decoration: none; }

      .conflict-toast {
        background: var(--tg-blue-bg, #ebf2ff);
        border: 1px solid var(--tg-blue-soft, rgba(0,128,255,0.4));
        color: var(--tg-blue-press, #0052cc);
        padding: 12px 16px; border-radius: 12px;
        margin-bottom: 16px;
        display: flex; align-items: center; gap: 12px; font-size: 13.5px;
      }
      .conflict-toast button {
        background: transparent; border: 0; color: inherit;
        font-family: inherit; font-weight: 700; cursor: pointer;
        margin-left: auto;
      }
      .terminal-banner {
        padding: 14px 18px; border-radius: 12px;
        margin-bottom: 16px; font-size: 13.5px;
      }
      .terminal-banner.warn {
        background: #fff8e1; color: #8b6100;
        border: 1px solid #f0d580;
      }
      .terminal-banner strong { display: inline; color: inherit; }

      .hero {
        display: flex; align-items: flex-start; justify-content: space-between;
        gap: 20px; margin-bottom: 24px;
      }
      .hero-l { min-width: 0; flex: 1; }
      .hero-back {
        display: inline-flex;
        align-items: center;
        margin-bottom: 12px;
        color: var(--tg-mute, #8f9eb0);
        text-decoration: none;
        font-size: 13px;
        font-weight: 600;
      }
      .hero-back:hover { color: var(--tg-ink-2, #333); }
      @media (max-width: 720px) {
        .hero { flex-direction: column; align-items: stretch; gap: 14px; }
        .hero-actions { flex-wrap: wrap; }
        .hero h1 { font-size: 22px !important; }
        .hero-back { margin-bottom: 10px; }
      }
      .hero-meta {
        display: flex; align-items: center; gap: 10px;
        flex-wrap: wrap; margin-bottom: 10px;
        font-size: 12px; color: var(--tg-mute, #8f9eb0); font-weight: 500;
      }
      .hero-meta-sep { width: 3px; height: 3px; border-radius: 50%; background: var(--tg-mute-2, #c4c4c8); }
      .hero-meta b { color: var(--tg-ink-2, #333); font-weight: 600; }
      .hero h1 {
        font-weight: 800; font-size: 28px; line-height: 1.15;
        letter-spacing: -0.02em; color: var(--tg-ink, #111);
        margin: 0; max-width: 760px;
      }
      .hero h1 .quiz-mark {
        font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
        text-transform: uppercase; color: var(--tg-blue, #0066ff);
        background: var(--tg-blue-bg, #ebf2ff);
        padding: 3px 8px; border-radius: 999px;
        margin-left: 10px; vertical-align: 5px;
      }
      .hero-actions { display: flex; gap: 8px; flex-shrink: 0; }
      .btn {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 9px 14px; border-radius: 10px;
        font-family: inherit; font-weight: 600; font-size: 13px;
        cursor: pointer; border: 1px solid transparent;
        white-space: nowrap; text-decoration: none;
      }
      .btn svg { width: 14px; height: 14px; }
      .btn-ghost {
        background: var(--tg-surface, #fff);
        color: var(--tg-ink-2, #333);
        border-color: var(--tg-line-2, #f0f0f4);
      }
      .btn-ghost:hover { background: var(--tg-surface-2, #f5f5f9); color: var(--tg-ink, #111); }
      .btn-danger-ghost {
        background: transparent; color: #dc3545; border-color: transparent;
      }
      .btn-danger-ghost:hover { background: #fff8e1; }

      .kpis {
        display: grid; grid-template-columns: repeat(4, 1fr);
        gap: 0; border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px; background: var(--tg-surface, #fff);
        overflow: hidden; margin-bottom: 24px;
      }
      .kpi { padding: 18px 22px; border-right: 1px solid var(--tg-line-2, #f0f0f4); min-width: 0; }
      .kpi:last-child { border-right: 0; }
      @media (max-width: 960px) {
        .kpis { grid-template-columns: repeat(2, 1fr); }
        .kpi:nth-child(2n) { border-right: 0; }
        .kpi:nth-child(-n+2) { border-bottom: 1px solid var(--tg-line-2, #f0f0f4); }
        .kpi:nth-child(n+3) { border-right: 1px solid var(--tg-line-2, #f0f0f4); }
        .kpi:nth-child(n+3):nth-child(2n) { border-right: 0; }
      }
      @media (max-width: 560px) {
        .kpis { grid-template-columns: 1fr; }
        .kpi { border-right: 0 !important; border-bottom: 1px solid var(--tg-line-2, #f0f0f4); padding: 14px 18px; }
        .kpi:last-child { border-bottom: 0; }
      }
      .kpi-label {
        font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
        color: var(--tg-mute, #8f9eb0);
        text-transform: uppercase; margin-bottom: 8px;
      }
      .kpi-value {
        font-weight: 800; font-size: 28px; line-height: 1;
        letter-spacing: -0.02em; color: var(--tg-ink, #111);
        font-variant-numeric: tabular-nums;
      }
      .kpi-value em {
        font-style: italic; font-weight: 400;
        color: var(--tg-blue, #0066ff);
        font-size: 0.7em; margin-left: 4px;
      }
      .kpi-sub { font-size: 12px; color: var(--tg-mute, #8f9eb0); margin-top: 6px; }
      .kpi-sub b { color: var(--tg-ink-2, #333); font-weight: 600; }

      .layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 22px; align-items: start; width: 100%; min-width: 0; }
      .layout > div, .side { min-width: 0; width: 100%; }
      @media (max-width: 1100px) {
        .layout { display: flex; flex-direction: column; gap: 16px; }
        /* На мобиле — «Параметры опроса» ВЫШЕ (иначе улетают в самый низ). */
        .side { order: -1; margin-bottom: 4px; position: static; top: auto; }
      }

      /* ── Mobile overflow fixes (Виктор 2026-07-28) ── */
      @media (max-width: 720px) {
        .poll-results { padding: 0 4px; }
        .card-head, .card-body { padding-left: 16px; padding-right: 16px; }
        .card { overflow: hidden; }
        /* Bars row: pct+count уходят под текст, не толкают горизонт */
        .result-text { flex-wrap: wrap; }
        .result-text-r { flex-shrink: 1; margin-left: auto; }
        .result-count { display: none; }
        .result-pct { font-size: 15px; }
        /* Voters row: time на 2-й строке при узости */
        .voter { grid-template-columns: 28px 1fr; }
        .voter-time { grid-column: 2; font-size: 11px; margin-top: 2px; }
        .voter-avatar { width: 28px; height: 28px; }
        /* Chart уменьшаем padding */
        .chart-svg { padding: 0 12px; height: 180px; }
        .chart-wrap { overflow-x: hidden; }
        /* Teaser card padding */
        .lab-teaser .card-body { padding: 20px 14px; }
      }

      .card {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px; overflow: hidden;
        margin-bottom: 16px;
        width: 100%;
        box-sizing: border-box;
      }
      .card-head {
        display: flex; align-items: center; gap: 12px;
        padding: 16px 22px; border-bottom: 1px solid var(--tg-line, #e8e8ec);
      }
      .card-title { font-weight: 700; font-size: 15px; color: var(--tg-ink, #111); margin: 0; }
      .card-sub { font-size: 12.5px; color: var(--tg-mute, #8f9eb0); margin: 2px 0 0; }
      .card-body { padding: 18px 22px 22px; }

      .results { display: flex; flex-direction: column; gap: 14px; }
      .result {
        display: grid; grid-template-columns: 24px 1fr; gap: 14px;
        align-items: flex-start;
      }
      .result-letter {
        width: 24px; height: 24px; border-radius: 50%;
        background: var(--tg-surface-2, #f5f5f9);
        display: flex; align-items: center; justify-content: center;
        font-size: 11px; font-weight: 700; color: var(--tg-mute, #8f9eb0);
        margin-top: 2px;
      }
      .result.winner .result-letter { background: var(--tg-up, #17a06a); color: white; }
      .result-body { min-width: 0; }
      .result-text {
        display: flex; align-items: baseline; justify-content: space-between;
        gap: 12px; font-size: 14.5px; color: var(--tg-ink, #111); margin-bottom: 7px;
      }
      .result-text-l {
        font-weight: 500; min-width: 0; display: flex; align-items: center; gap: 8px;
        flex: 1; overflow-wrap: anywhere;
      }
      .result.winner .result-text-l { font-weight: 700; }
      .correct-tag {
        font-size: 10px; font-weight: 700; letter-spacing: 0.05em;
        text-transform: uppercase; color: var(--tg-up, #17a06a);
        background: rgba(23,160,106,0.10); padding: 2px 6px; border-radius: 999px;
      }
      .result-text-r {
        display: flex; align-items: baseline; gap: 8px;
        flex-shrink: 0; font-variant-numeric: tabular-nums;
      }
      .result-pct { font-weight: 800; font-size: 16px; color: var(--tg-ink, #111); }
      .result.winner .result-pct { color: var(--tg-up, #17a06a); }
      .result-count { font-size: 12px; color: var(--tg-mute, #8f9eb0); font-weight: 500; }
      .result-bar {
        height: 8px; background: var(--tg-line, #e8e8ec);
        border-radius: 999px; overflow: hidden; position: relative;
      }
      .result-bar i {
        position: absolute; inset: 0; width: 0%;
        background: var(--tg-blue, #0066ff); border-radius: inherit;
        transition: width 400ms ease-out;
      }
      .result.winner .result-bar i { background: var(--tg-up, #17a06a); }

      .chart-wrap { padding: 8px 0 4px; }
      .chart-svg { width: 100%; height: 200px; display: block; padding: 0 22px; box-sizing: border-box; }
      .chart-legend {
        display: flex; align-items: center; gap: 16px;
        padding: 12px 22px 18px; border-top: 1px solid var(--tg-line, #e8e8ec);
        flex-wrap: wrap; font-size: 12px; color: var(--tg-mute, #8f9eb0);
      }
      .chart-legend-i { display: inline-flex; align-items: center; gap: 6px; }
      .swatch { width: 12px; height: 3px; border-radius: 2px; }

      .tabs {
        display: flex; gap: 4px; padding: 3px;
        background: var(--tg-surface-2, #f5f5f9); border-radius: 10px;
        margin-bottom: 12px; overflow-x: auto; flex-wrap: nowrap;
      }
      .tabs button {
        padding: 7px 11px; border: 0; background: transparent;
        cursor: pointer; font-family: inherit; font-weight: 600; font-size: 12.5px;
        color: var(--tg-mute, #8f9eb0); border-radius: 7px;
        white-space: nowrap; display: inline-flex; align-items: center; gap: 6px;
      }
      .tabs button.on { background: white; color: var(--tg-ink, #111); box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
      .tab-count { font-size: 10.5px; font-weight: 700; color: var(--tg-mute-2, #c4c4c8); }
      .tabs button.on .tab-count { color: var(--tg-blue, #0066ff); }
      @media (max-width: 640px) {
        .tabs {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          overflow: visible;
        }
        .tabs button:first-child {
          grid-column: 1 / -1;
        }
        .tabs button {
          min-width: 0;
          justify-content: center;
          text-align: center;
          white-space: normal;
          line-height: 1.2;
          min-height: 40px;
        }
      }

      .anon-note {
        padding: 12px 14px; background: var(--tg-bg, #fafafd);
        border: 1px dashed var(--tg-line, #e8e8ec); border-radius: 10px;
        font-size: 12.5px; color: var(--tg-mute, #8f9eb0); line-height: 1.5;
        margin-bottom: 12px;
      }
      .anon-note b { color: var(--tg-ink-2, #333); font-weight: 600; }

      .voters-list { display: flex; flex-direction: column; }
      .voter {
        display: grid; grid-template-columns: 32px 1fr auto; gap: 10px;
        align-items: center; padding: 10px 0;
        border-bottom: 1px solid var(--tg-line, #e8e8ec);
      }
      .voter:last-child { border-bottom: 0; }
      .voter-avatar {
        width: 32px; height: 32px; border-radius: 50%;
        background: linear-gradient(135deg, #4d9fff, #7c3aed);
        display: flex; align-items: center; justify-content: center;
        color: white; font-weight: 700; font-size: 12px;
      }
      .voter-avatar.anon {
        background: var(--tg-bg, #fafafd);
        color: var(--tg-mute-2, #c4c4c8);
        border: 1.5px dashed var(--tg-line, #e8e8ec);
      }
      .voter-name { font-weight: 600; font-size: 13.5px; color: var(--tg-ink, #111); overflow-wrap: anywhere; }
      .voter-sub { font-size: 11.5px; color: var(--tg-mute, #8f9eb0); margin-top: 2px; overflow-wrap: anywhere; }
      .voter-sub b { color: var(--tg-ink-2, #333); font-weight: 600; }
      .voter-time { font-size: 11.5px; color: var(--tg-mute, #8f9eb0); font-variant-numeric: tabular-nums; }

      .side { position: static; top: auto; }
      .side-panel {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px; padding: 18px 22px;
        width: 100%;
        box-sizing: border-box;
      }
      .side-title { font-weight: 700; font-size: 14px; color: var(--tg-ink, #111); margin: 0 0 12px; }
      .meta-list { display: flex; flex-direction: column; gap: 10px; font-size: 13px; }
      .meta-row { display: flex; justify-content: space-between; gap: 10px; min-width: 0; }
      .meta-row .k { color: var(--tg-mute, #8f9eb0); }
      .meta-row .v {
        color: var(--tg-ink, #111); font-weight: 600; font-variant-numeric: tabular-nums; text-align: right;
        min-width: 0; overflow-wrap: anywhere;
      }

      .list-empty {
        padding: 24px; text-align: center; color: var(--tg-mute, #8f9eb0); font-size: 13px;
      }
      .load-more {
        margin-top: 12px; width: 100%;
        padding: 10px 14px; border-radius: 10px;
        border: 1px solid var(--tg-line-2, #f0f0f4);
        background: var(--tg-surface, #fff); color: var(--tg-ink-2, #333);
        font-family: inherit; font-weight: 600; font-size: 13px;
        cursor: pointer;
      }
      .load-more:hover:not(:disabled) { background: var(--tg-surface-2, #f5f5f9); }
      .load-more:disabled { opacity: 0.6; cursor: default; }

      .lab-teaser .card-body { padding: 28px 22px; }
      .teaser-body {
        display: flex; flex-direction: column; align-items: center; gap: 12px;
        text-align: center;
      }
      .teaser-icon {
        width: 44px; height: 44px; border-radius: 12px;
        background: var(--tg-blue-bg, #ebf2ff); color: var(--tg-blue-press, #0052cc);
        display: flex; align-items: center; justify-content: center;
      }
      .teaser-icon svg { width: 22px; height: 22px; }
      .teaser-text {
        font-size: 13.5px; color: var(--tg-ink-2, #333); margin: 0;
        line-height: 1.5; max-width: 460px;
      }
      .teaser-cta {
        display: inline-flex; align-items: center;
        padding: 10px 18px; border-radius: 10px;
        background: var(--tg-blue, #0066ff); color: white;
        font-weight: 600; font-size: 13.5px; text-decoration: none;
        margin-top: 4px;
      }
      .teaser-cta:hover { background: var(--tg-blue-hover, #0052cc); }
    `}</style>
  )
}
